import { type FilterBy } from "@/pages/project/features/directory/store/models";
import { type ViewBy } from "@/pages/project/features/directory/views/myProjects/models";
import { DefaultApi, EventName, Plan, type QuizAttempt, type Track } from "@/api";
import { Store } from "@/store";
import { initApi } from "@/utils";

export interface EventDataType {
  [key: string]: unknown;
}

export class AnalyticsService {
  private readonly api: DefaultApi;

  constructor(api: DefaultApi = initApi({ useSendBeacon: true })) {
    this.api = api;
  }

  public trackProjectViewed(projectId: string): void {
    this.track(EventName.EVENT_PROJECT_VIEWED, { projectId });
  }

  public trackProjectComplete(projectId: string): void {
    this.track(EventName.EVENT_PROJECT_COMPLETED, { description: "Project completed!", project_id: projectId });
  }

  /**
   * Tracks when the user clicks the "Next Project" button in the Project Complete modal
   */
  public trackNextProjectClicked(currentProjectId: string, nextProjectId: string): void {
    this.track(EventName.EVENT_PROJECT_NEXT_PROJECT_CLICKED, { currentProjectId, nextProjectId });
  }

  /**
   * Tracks when the user attempts to reset a project
   * @param projectId - The id of the project
   * @param hasAccess - Whether the user has access to reset the project or not
   */
  public trackProjectResetModalOpened(projectId: string): void {
    this.track(EventName.EVENT_PROJECT_RESET_MODAL_OPENED, { projectId });
  }

  /**
   * Tracks when the user attempts to trigger the reset of a project
   * @param projectId - The id of the project
   * @param hasAccess - Whether the user has access to reset the project or not
   */
  public trackProjectResetTriggered(projectId: string, hasAccess: boolean): void {
    this.track(EventName.EVENT_PROJECT_RESET_TRIGGERED, { projectId, hasAccess });
  }

  /**
   * Tracks when the user clicks a share option in the project share modal
   * @param projectId - The id of the project
   * @param option - The option that was clicked
   */
  public trackProjectShareOptionClicked(projectId: string, option: string): void {
    this.track(EventName.EVENT_PROJECT_SHARE_OPTION_CLICKED, { projectId, option });
  }

  /**
   * Tracks when the user clicks the "Discover more" button in the Project Discover modal
   * @param projectId - The id of the project
   */
  public trackProjectDiscoverMoreClicked(projectId: string): void {
    this.track(EventName.EVENT_PROJECT_DISCOVER_MORE_CLICKED, { projectId });
  }

  public trackProClicked(isPro: boolean, utm_medium: string, additionalData: Record<string, string> = {}): void {
    this.track(EventName.EVENT_PRO_CLICKED, { isPro, utm_medium, ...additionalData });
  }

  public trackCheckoutCancelled(): void {
    this.track(EventName.EVENT_CHECKOUT_CANCELLED);
  }

  public trackCheckoutSuccess(): void {
    this.track(EventName.EVENT_CHECKOUT_SUCCESS);
  }

  public trackJumpTo(type: "step" | "validation_box" | "bookmark" | "generic", projectId: string, id: string): void {
    this.track(EventName.EVENT_NAVIGATION_SCROLLED_TO, { type, id, projectId, source: "table-of-content" });
  }

  public trackSidebarOpen(source: "hover" | "toolbar" | "share-modal" | "close-btn" | undefined): void {
    this.track(EventName.EVENT_NAVIGATION_SIDEBAR_OPENED, { source });
  }

  /**
   * Tracks when the user copies the invite link from a given source
   * @param source - The source of the invite link
   */
  public trackInviteLinkCopied(source: "user-menu" | "project-completions"): void {
    this.track(EventName.EVENT_INVITE_LINK_COPIED, { source });
  }

  /**
   * Tracks when the user clicks an internal content link
   * @param projectId - The id of the project
   * @param to - The id to where the user was redirected to
   */
  public trackContentLinkClicked(projectId: string, to: string): void {
    this.track(EventName.EVENT_INTERNAL_CONTENT_LINK_CLICKED, { projectId, to });
  }

  /**
   * Tracks when the user updates their profile information
   * @param type - The field tha twas updated, username or user profile avatar
   * @param source - Where the update occurred
   */
  public trackUserProfileUpdated(type: "username" | "avatar", source: "user-menu"): void {
    this.track(EventName.EVENT_PROFILE_UPDATED, { type, source });
  }

  public trackImageZoomed(projectId: string, src: string): void {
    this.track(EventName.EVENT_IMAGE_ZOOMED, { projectId, src });
  }

  public trackCodeSnippetCopied(source: "inline" | "block", projectId: string, codeContent: string): void {
    this.track(EventName.EVENT_CODE_SNIPPET_COPIED, { source, projectId, codeContent });
  }

  public trackDocumentationViewed(projectId: string): void {
    this.track(EventName.EVENT_DOCUMENTATION_VIEWED, { projectId });
  }

  public trackSocialShareClicked(projectId: string, platform: string): void {
    this.track(EventName.EVENT_SOCIAL_SHARE_CLICKED, { projectId, platform });
  }

  public trackProjectTrackSelected(projectId: string, track: Track["id"]): void {
    this.track(EventName.EVENT_PROJECT_TRACK_CLICKED, { projectId, track });
  }

  /**
   * Tracks when a series tile in the directory is clicked on and viewed by the user
   * @param seriesId - The id of the series
   */
  public trackSeriesViewed(seriesId: string): void {
    this.track(EventName.EVENT_DIRECTORY_SERIES_VIEWED, { seriesId });
  }

  public trackValidationBoxActionClicked(projectId: string, boxId: string, action: "done" | "mark for review"): void {
    this.track(EventName.EVENT_VALIDATION_BOX_ACTION_CLICKED, { projectId, boxId, action });
  }

  public trackTimeSwitcherTimeChanged(hoursPerDay: number, daysPerWeek: number): void {
    this.track(EventName.EVENT_TIME_SWITCHER_TIME_CHANGED, { hoursPerDay, daysPerWeek });
  }

  public trackTimeSwitcherTopicChanged(topics: string[]): void {
    this.track(EventName.EVENT_TIME_SWITCHER_TOPIC_CHANGED, { topics });
  }

  public trackEditorHighlightAdded(color: string, highlightedContent: string): void {
    this.track(EventName.EVENT_EDITOR_HIGHLIGHT_ADDED, { color, highlightedContent });
  }

  public trackEditorHighlightRemoved(highlightedContent: string): void {
    this.track(EventName.EVENT_EDITOR_HIGHLIGHT_REMOVED, { highlightedContent });
  }

  public trackEditorAIAction(
    type: "createBlank" | "simplify" | "explain" | "examples" | "ask",
    group: string,
    highlightedContent: string,
    surroundingContent: string,
    aiResult: string,
    query?: string,
  ): void {
    switch (type) {
      case "simplify":
        return this.track(EventName.EVENT_EDITOR_SIMPLIFIED, {
          group,
          highlightedContent,
          surroundingContent,
          aiResult,
        });
      case "explain":
        return this.track(EventName.EVENT_EDITOR_EXPLAINED, {
          group,
          highlightedContent,
          surroundingContent,
          aiResult,
        });
      case "examples":
        return this.track(EventName.EVENT_EDITOR_EXAMPLES, {
          group,
          highlightedContent,
          surroundingContent,
          aiResult,
        });
      case "createBlank":
        return this.track(EventName.EVENT_EDITOR_BLANK_NOTE_CREATED, {
          group,
          highlightedContent,
          surroundingContent,
        });
      case "ask":
        return this.track(EventName.EVENT_EDITOR_ASKED, {
          group,
          highlightedContent,
          surroundingContent,
          aiResult,
          query,
        });
    }
  }

  public trackDirectoryTabChanged(tab: string): void {
    this.track(EventName.EVENT_DIRECTORY_TAB_CHANGED, { tab });
  }

  /**
   * Tracks when the user adds a series or project to their "My projects".
   @param type - Whether the item is a series or project
   @param entities - Items that were actioned
   @param action - The action that was taken (added or removed)
   */
  public trackDirectoryAddToMyProjectsClicked(
    type: "series" | "project",
    entities: string[],
    action: "added" | "removed",
  ): void {
    this.track(EventName.EVENT_DIRECTORY_ADD_TO_MY_PROJECTS_CLICKED, {
      type,
      entities,
      action,
    });
  }

  /**
   * Tracks when the user changes the view of their "My projects" tab
   * @param view - The view that the user changed to
   */
  public trackMyProjectsViewChanged(view: ViewBy): void {
    this.track(EventName.EVENT_MY_PROJECTS_VIEW_CHANGED, { view });
  }

  /**
   * Tracks when the user changes the filter of their "My projects" tab
   * @param filter - The filter that the user changed to
   */
  public trackMyProjectsFilterChanged(filter: FilterBy): void {
    this.track(EventName.EVENT_MY_PROJECTS_FILTER_CHANGED, { filter });
  }

  /**
   * Tracks when the user changes the sort of their "My projects" tab
   * @param sortBy - The sort that the user changed to
   * @param orderBy - The order that the user changed to
   */
  public trackMyProjectsSortChanged(
    sortBy: "asc" | "desc",
    orderBy: "Recently added" | "Topics (A-Z)" | "Recommended",
  ): void {
    this.track(EventName.EVENT_MY_PROJECTS_SORT_CHANGED, { sortBy, orderBy });
  }

  /**
   * Tracks when the user clicks the "Add Project" button in the My Projects directory view
   */
  public trackMyProjectsAddProjectClicked(): void {
    this.track(EventName.EVENT_MY_PROJECTS_ADD_PROJECT_CLICKED);
  }

  public trackMobileBannerRemindMeClicked(): void {
    this.track(EventName.EVENT_MOBILE_BANNER_REMIND_ME_CLICKED);
  }

  /**
   * Tracks when a nav item is clicked
   * @param name - The name of the nav item
   */
  public trackNavItemClicked(name: string): void {
    this.track(EventName.EVENT_NAV_ITEM_CLICKED, { name });
  }

  public trackPortfolioFeatureRating(rating: string): void {
    this.track(EventName.EVENT_PORTFOLIO_COMING_SOON_RATING_CLICKED, { rating });
  }

  public trackProjectUnlocked(projectId: string): void {
    this.track(EventName.EVENT_PROJECT_UNLOCKED, { projectId });
  }

  public trackProSectionUnlocked(id: string): void {
    this.track(EventName.EVENT_PRO_SECTION_UNLOCKED, { sectionId: id });
  }

  /**
   * Tracks when the user opens or closes a section
   * @param sectionId - The id of the section
   * @param state - The state of the section
   */
  public trackSectionToggled(sectionId: string, state: "opened" | "closed"): void {
    this.track(EventName.EVENT_SECTION_TOGGLED, { sectionId, state });
  }

  public trackPortfolioLinkCopied(): void {
    this.track(EventName.EVENT_PORTFOLIO_LINK_COPIED);
  }

  public trackPortfolioDocumentAdded(documentId: string): void {
    this.track(EventName.EVENT_PORTFOLIO_DOCUMENT_ADDED, { documentId });
  }

  public trackPortfolioDocumentDeleted(documentId: string): void {
    this.track(EventName.EVENT_PORTFOLIO_DOCUMENT_DELETED, { documentId });
  }

  public trackPortfolioDetailsUpdated(title: string, description: string): void {
    this.track(EventName.EVENT_PORTFOLIO_DETAILS_UPDATED, { title, description });
  }

  public trackPortfolioDocumentEdited(documentId: string): void {
    this.track(EventName.EVENT_PORTFOLIO_DOCUMENT_EDITED, { documentId });
  }

  public trackPortfolioDocumentViewed(documentId: string): void {
    this.track(EventName.EVENT_PORTFOLIO_DOCUMENT_VIEWED, { documentId });
  }

  /**
   * Tracks when the paywall is triggered
   * @param triggeredBy - The source of the paywall trigger
   * @param extraInfo - Extra information about the paywall trigger
   */
  public trackPaywallTriggered(triggeredBy: string, extraInfo: Record<string, string>): void {
    this.track(EventName.EVENT_PAYWALL_TRIGGERED, {
      triggeredBy,
      ...extraInfo,
    });
  }

  /**
   * Tracks when the user toggles the billing cycle
   * @param billingPeriod - The billing cycle
   */
  public trackPaywallBillingPeriodToggled(billingPeriod: string): void {
    this.track(EventName.EVENT_PAYWALL_BILLING_PERIOD_TOGGLED, { to: billingPeriod });
  }

  /**
   * Tracks when the user clicks the paywall button
   * @param buttonName - The button that was clicked
   */
  public trackPaywallButtonClicked(buttonName: string): void {
    this.track(EventName.EVENT_PAYWALL_BUTTON_CLICKED, { buttonName });
  }

  /**
   * Tracks when the user clicks a documentation theme
   * @param theme - The theme that was clicked
   */
  public trackDocumentationThemeClicked(theme: string): void {
    this.track(EventName.EVENT_DOCUMENTATION_THEME_CLICKED, { theme });
  }

  /**
   * Tracks when the search bar is prefilled by us through query string or other means.
   * @param searchTerm - The search term
   */
  public trackPrefilledSearch(searchTerm: string): void {
    this.track(EventName.EVENT_SEARCH_PREFILLED, { searchTerm });
  }

  public trackValidationEdited(projectId: string, validationId: string): void {
    this.track(EventName.EVENT_VALIDATION_EDITED, { projectId, validationId });
  }

  public trackContentTabsChanged(
    projectId: string,
    fromTabId: string,
    fromValue: string,
    toTabId: string,
    toValue: string,
  ): void {
    this.track(EventName.EVENT_CONTENT_TABS_CHANGED, { projectId, fromTabId, toTabId, fromValue, toValue });
  }

  public trackUserSyncInfoUpdated(from: Plan | undefined, to: Plan | undefined): void {
    this.track(EventName.EVENT_USER_SYNC_INFO_UPDATED, { from, to });
  }

  public trackQuizStart(quizId: string, projectId: string): void {
    this.track(EventName.EVENT_QUIZ_STARTED, { projectId, quizId });
  }

  public trackQuizQuestionAnswered(quizId: string, projectId: string, questionId: string, optionId: string): void {
    this.track(EventName.EVENT_QUIZ_QUESTION_ANSWERED, { projectId, quizId, questionId, optionId });
  }

  public trackQuizCompleted(quizId: string, projectId: string, attempt: QuizAttempt): void {
    this.track(EventName.EVENT_QUIZ_COMPLETED, { projectId, quizId, attempt });
  }

  public trackBookmarkToggled(projectId: string, nodeId: string, state: "added" | "removed"): void {
    this.track(EventName.EVENT_BOOKMARK_TOGGLED, { projectId, nodeId, state });
  }

  /**
   * Track the user's feedback on a given feature
   * @param feature
   * @param feedback - The user's feedback on a given feature
   * @param analyticsOptions
   */
  public trackFeedbackClicked(feature: string, feedback: "good" | "bad", analyticsOptions?: EventDataType): void {
    this.track(EventName.EVENT_FEEDBACK_CLICKED, { feature, feedback, ...analyticsOptions });
  }

  /**
   * Tracks when the user pins or unpins a ui element
   * @param projectId - The id of the project
   * @param target - The target of the pin toggle
   * @param state - The state of the pin toggle
   */
  public trackPinToggled(
    projectId: string,
    target: "bubble-bar" | "table-of-content",
    state: "pinned" | "unpinned",
  ): void {
    this.track(EventName.EVENT_PIN_TOGGLED, { projectId, target, state });
  }

  private track(eventName: EventName, details: EventDataType = {}): void {
    try {
      if (Store.app.isImpersonating.value) {
        // We don't want to log analytics events when impersonating
        return;
      }

      this.api?.recordEvent({ event: { name: eventName, data: details } });
    } catch (error) {
      // Swallow the error for now, analytics scripts should not break the app.
      console.log(error);
    }
  }
}

const getAnalyticsService = (() => {
  let instance: AnalyticsService | undefined;

  return (): AnalyticsService => {
    if (!instance) {
      instance = new AnalyticsService();
    }
    return instance;
  };
})();

export function useAnalyticsService(): AnalyticsService {
  return getAnalyticsService();
}
